import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query, 
  where,
  orderBy,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Friend } from '../types/friend';
import { FriendRequest } from '../types/friendRequest';
import { User } from './userService';

/**
 * Search users by Wiin ID or Username
 * Returns users matching the search term (case-insensitive)
 */
export const searchUsersByWiinIdOrUsername = async (
  searchTerm: string,
  currentUserId: string
): Promise<User[]> => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }

    const searchTermLower = searchTerm.toLowerCase().trim();
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    const matchingUsers: User[] = [];
    
    usersSnapshot.forEach((doc) => {
      const userData = doc.data() as User;
      
      // Don't include current user in search results
      if (userData.uid === currentUserId) {
        return;
      }
      
      const username = (userData.brugernavn || '').toLowerCase();
      const wiinId = (userData.Wiin_ID || '').toLowerCase();
      
      if (username.includes(searchTermLower) || wiinId.includes(searchTermLower)) {
        matchingUsers.push(userData);
      }
    });
    
    return matchingUsers;
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Send a friend request
 */
export const sendFriendRequest = async (
  senderId: string,
  senderUsername: string,
  senderWiinId: string,
  receiverId: string,
  receiverUsername: string
): Promise<string> => {
  try {
    // Check if sender is blocked by receiver
    const isBlocked = await checkIfBlocked(receiverId, senderId);
    if (isBlocked) {
      throw new Error('Cannot send friend request - you are blocked by this user');
    }
    
    // Check if they are already friends
    const friendDoc = await getDoc(doc(db, `users/${senderId}/friends/${receiverId}`));
    if (friendDoc.exists()) {
      throw new Error('Already friends with this user');
    }
    
    // Check if there's already a pending request
    const requestsRef = collection(db, 'friend_requests');
    const q = query(
      requestsRef,
      where('senderId', '==', senderId),
      where('receiverId', '==', receiverId),
      where('status', '==', 'pending')
    );
    const existingRequests = await getDocs(q);
    
    if (!existingRequests.empty) {
      throw new Error('Friend request already sent');
    }
    
    // Create friend request
    const requestData: Omit<FriendRequest, 'id'> = {
      senderId,
      senderUsername,
      senderWiinId,
      receiverId,
      receiverUsername,
      status: 'pending',
      createdAt: new Date()
    };
    
    const docRef = await addDoc(requestsRef, requestData);
    return docRef.id;
  } catch (error) {
    console.error('Error sending friend request:', error);
    throw error;
  }
};

/**
 * Accept a friend request
 * Creates friendship in both users' subcollections
 */
export const acceptFriendRequest = async (requestId: string): Promise<void> => {
  try {
    const requestRef = doc(db, 'friend_requests', requestId);
    const requestDoc = await getDoc(requestRef);
    
    if (!requestDoc.exists()) {
      throw new Error('Friend request not found');
    }
    
    const request = requestDoc.data() as FriendRequest;
    
    if (request.status !== 'pending') {
      throw new Error('Friend request is not pending');
    }
    
    // Get sender and receiver user data
    const senderDoc = await getDoc(doc(db, 'users', request.senderId));
    const receiverDoc = await getDoc(doc(db, 'users', request.receiverId));
    
    if (!senderDoc.exists() || !receiverDoc.exists()) {
      throw new Error('User not found');
    }
    
    const senderData = senderDoc.data() as User;
    const receiverData = receiverDoc.data() as User;
    
    // Create friendship in sender's friends subcollection
    const senderFriendData: Friend = {
      id: request.receiverId,
      username: receiverData.brugernavn || `${receiverData.fornavn} ${receiverData.efternavn}`,
      wiinId: receiverData.Wiin_ID,
      status: 'active',
      addedAt: new Date()
    };
    
    await setDoc(
      doc(db, `users/${request.senderId}/friends/${request.receiverId}`),
      senderFriendData
    );
    
    // Create friendship in receiver's friends subcollection
    const receiverFriendData: Friend = {
      id: request.senderId,
      username: senderData.brugernavn || `${senderData.fornavn} ${senderData.efternavn}`,
      wiinId: senderData.Wiin_ID,
      status: 'active',
      addedAt: new Date()
    };
    
    await setDoc(
      doc(db, `users/${request.receiverId}/friends/${request.senderId}`),
      receiverFriendData
    );
    
    // Update request status
    await updateDoc(requestRef, {
      status: 'accepted'
    });
    
  } catch (error) {
    console.error('Error accepting friend request:', error);
    throw error;
  }
};

/**
 * Reject a friend request
 */
export const rejectFriendRequest = async (requestId: string): Promise<void> => {
  try {
    const requestRef = doc(db, 'friend_requests', requestId);
    const requestDoc = await getDoc(requestRef);
    
    if (!requestDoc.exists()) {
      throw new Error('Friend request not found');
    }
    
    await updateDoc(requestRef, {
      status: 'rejected'
    });
  } catch (error) {
    console.error('Error rejecting friend request:', error);
    throw error;
  }
};

/**
 * Get all friends for a user
 */
export const getFriendsList = async (userId: string): Promise<Friend[]> => {
  try {
    const friendsRef = collection(db, `users/${userId}/friends`);
    const friendsSnapshot = await getDocs(friendsRef);
    
    const friends: Friend[] = [];
    friendsSnapshot.forEach((doc) => {
      const friendData = doc.data();
      friends.push({
        id: doc.id,
        username: friendData.username,
        wiinId: friendData.wiinId,
        status: friendData.status || 'active',
        addedAt: friendData.addedAt?.toDate?.() || new Date()
      });
    });
    
    // Sort alphabetically by username
    friends.sort((a, b) => a.username.localeCompare(b.username));
    
    return friends;
  } catch (error) {
    console.error('Error getting friends list:', error);
    throw error;
  }
};

/**
 * Get pending friend requests received by user (incoming)
 */
export const getPendingFriendRequests = async (userId: string): Promise<FriendRequest[]> => {
  try {
    const requestsRef = collection(db, 'friend_requests');
    const q = query(
      requestsRef,
      where('receiverId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    const requests: FriendRequest[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      requests.push({
        id: doc.id,
        senderId: data.senderId,
        senderUsername: data.senderUsername,
        senderWiinId: data.senderWiinId,
        receiverId: data.receiverId,
        receiverUsername: data.receiverUsername,
        status: data.status,
        createdAt: data.createdAt?.toDate?.() || new Date()
      });
    });
    
    return requests;
  } catch (error) {
    console.error('Error getting pending friend requests:', error);
    throw error;
  }
};

/**
 * Get sent friend requests by user (outgoing)
 */
export const getSentFriendRequests = async (userId: string): Promise<FriendRequest[]> => {
  try {
    const requestsRef = collection(db, 'friend_requests');
    const q = query(
      requestsRef,
      where('senderId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(q);
    const requests: FriendRequest[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      requests.push({
        id: doc.id,
        senderId: data.senderId,
        senderUsername: data.senderUsername,
        senderWiinId: data.senderWiinId,
        receiverId: data.receiverId,
        receiverUsername: data.receiverUsername,
        status: data.status,
        createdAt: data.createdAt?.toDate?.() || new Date()
      });
    });
    
    return requests;
  } catch (error) {
    console.error('Error getting sent friend requests:', error);
    throw error;
  }
};

/**
 * Remove a friend (deletes friendship from both users)
 */
export const removeFriend = async (userId: string, friendId: string): Promise<void> => {
  try {
    // Delete from user's friends
    await deleteDoc(doc(db, `users/${userId}/friends/${friendId}`));
    
    // Delete from friend's friends
    await deleteDoc(doc(db, `users/${friendId}/friends/${userId}`));
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
};

/**
 * Block a friend (pauses friendship)
 */
export const blockFriend = async (userId: string, friendId: string): Promise<void> => {
  try {
    const friendRef = doc(db, `users/${userId}/friends/${friendId}`);
    await updateDoc(friendRef, {
      status: 'blocked'
    });
  } catch (error) {
    console.error('Error blocking friend:', error);
    throw error;
  }
};

/**
 * Unblock a friend
 */
export const unblockFriend = async (userId: string, friendId: string): Promise<void> => {
  try {
    const friendRef = doc(db, `users/${userId}/friends/${friendId}`);
    await updateDoc(friendRef, {
      status: 'active'
    });
  } catch (error) {
    console.error('Error unblocking friend:', error);
    throw error;
  }
};

/**
 * Check if a user is blocked by another user
 */
export const checkIfBlocked = async (userId: string, potentialFriendId: string): Promise<boolean> => {
  try {
    const friendRef = doc(db, `users/${userId}/friends/${potentialFriendId}`);
    const friendDoc = await getDoc(friendRef);
    
    if (!friendDoc.exists()) {
      return false;
    }
    
    const friendData = friendDoc.data() as Friend;
    return friendData.status === 'blocked';
  } catch (error) {
    console.error('Error checking block status:', error);
    return false;
  }
};

/**
 * Get active (non-blocked) friends for prize sharing
 */
export const getActiveFriends = async (userId: string): Promise<Friend[]> => {
  try {
    const allFriends = await getFriendsList(userId);
    return allFriends.filter(friend => friend.status === 'active');
  } catch (error) {
    console.error('Error getting active friends:', error);
    throw error;
  }
};
